import { useState } from 'react';
import { ScreenSkeleton } from '../components/atoms/ScreenSkeleton';
import { useAtomValue } from 'jotai';
import {
  inspectionAtom,
  isPermissionDeniedError,
} from '../store/inspection/inspectionAtoms';
import { inspectionDB } from '../data/database.structure';
import { InspectionCard } from '../components/molecules/InspectionCard';
import { useLiveQueryCached } from '../hooks/useLiveQueryCached';
import { Button } from '../components/atoms/Button';
import { SectionHeading } from '../components/atoms/SectionHeading';
import { inspectionMatchesSearch } from '../helpers/inspectionSearch';
import { useNavigate } from 'react-router';
import { routePaths } from '../routes/basePaths';
import FloatingButton from '../components/atoms/FloatingButton';
import Modal from '../components/molecules/Modal';
import { useInitialDataLoaded } from '../hooks/useInitialDataLoaded';
import { InspectionViewModel } from '../data/viewmodels.types';
import DatabaseMineLocationMethods from '../data/database.minelocations.methods';
import LoadingPage from './LoadingPage';
import { loadingInspectionMessageAtom } from '../store/loadingInspectionProgressMessage';
import InspectionProgressMessage from '../store/loadingInspectionProgressMessage';
import { LoadingIndicator } from '../components/atoms/LoadingIndicator';
import DatabaseAssetsMethods from '../data/database.assets.methods';
import { useSignOut } from '../hooks/useSignOut';
import { formatCount } from '../helpers/syncResultHelpers';
import { inspectionService } from '../services/inspection/inspection.service';
import { inspectionRecordService } from '../services/inspection/inspectionRecordService';
import { SortDirection, SortingType } from '../components/atoms/SortingMenu';
import SearchWithSorting from '../components/molecules/SearchWithSorting';

/** Cards rendered per page (Show more adds another page). The full ~1,100-card
 *  list froze the tab when rendered at once; search/sort still operate over the
 *  FULL list - only rendering is paged. */
const PAGE_SIZE = 50;

/**
 * THE front page - the old /history screen folded in (its tabs, search, sort
 * and paged list), so there is one place to find an inspection instead of two.
 */
export function WelcomeScreen() {
  // Cached wrapper: plain useLiveQuery is undefined on every mount's first render,
  // which made the list render empty then pop in on every return to Home.
  const inspections = useLiveQueryCached('welcome-inspections', async () => {
    const all = await inspectionDB.inspections
      .orderBy('inspectionDate')
      .reverse()
      .toArray();
    // PERF: join only the assets/mine-locations these inspections reference,
    // via Maps - the old history screen joined the ENTIRE fleet on every
    // inspections write.
    const assets = await new DatabaseAssetsMethods(inspectionDB).getAssetsByIds(
      {
        assetIds: [...new Set(all.map((i) => i.assetId))],
        includeEquipmentParts: false,
      },
    );
    const assetById = new Map(assets.map((a) => [a.id, a]));
    const mineLocations = await new DatabaseMineLocationMethods(
      inspectionDB,
    ).getMineLocationsWithDataByIds([
      ...new Set(all.map((i) => i.mineLocationId)),
    ]);
    const mineLocationById = new Map(
      mineLocations.map((ml) => [ml.mineLocation.id, ml]),
    );

    return all.map((inspection) => ({
      inspection,
      asset: assetById.get(inspection.assetId),
      mineLocation: mineLocationById.get(inspection.mineLocationId),
    })) as InspectionViewModel[];
  });

  // A failed sync keeps the local data and shows the banner instead of the sign-out
  // page while the device still holds a completed sync. The query having no data says
  // nothing about that: "Refresh data" recreates it empty once it is garbage-collected.
  // A denied sync is fatal either way: the user can no longer push their inspections.
  // While "Try again" runs, the query keeps its error, so the page gives way to the sync's progress.
  const { isError, error, isLoading, isFetching, refetch } =
    useAtomValue(inspectionAtom);
  const isPermissionError = isPermissionDeniedError(error);
  const hasNoCompletedSync = inspectionService.getLastSyncDate() === null;
  const showLoadFailurePage =
    isPermissionError || (!isFetching && isError && hasNoCompletedSync);
  const [isOpenModal, setOpenModal] = useState(false);
  const { signOut, requestSignOut, signOutDialog } = useSignOut();
  const [searchTerm, setSearchTerm] = useState('');
  const [inProgressVisible, setInProgressVisible] = useState(PAGE_SIZE);
  const [submittedVisible, setSubmittedVisible] = useState(PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState<{
    key: SortingType;
    direction: SortDirection;
  }>({ key: SortingType.date, direction: SortDirection.desc });
  const navigate = useNavigate();
  const initialDataLoadedState = useInitialDataLoaded();

  const inspectionProgressMessage = useAtomValue(loadingInspectionMessageAtom);

  // ONE search over everything (an inspector looking for an inspection should
  // not have to know which pile it landed in), then the two piles: the working
  // set first, submitted below. Matcher extracted + trimmed - see helper.
  const matchesSearch = (model: InspectionViewModel) =>
    inspectionMatchesSearch(model, searchTerm);

  const bySortConfig = (a: InspectionViewModel, b: InspectionViewModel) => {
    if (sortConfig.key === SortingType.date) {
      const dateA = new Date(a.inspection.inspectionDate).getTime();
      const dateB = new Date(b.inspection.inspectionDate).getTime();
      return sortConfig.direction === SortDirection.asc
        ? dateA - dateB
        : dateB - dateA;
    }
    const nameA = (
      a.asset?.name ??
      a.mineLocation?.mineLocation?.name ??
      ''
    ).toLowerCase();
    const nameB = (
      b.asset?.name ??
      b.mineLocation?.mineLocation?.name ??
      ''
    ).toLowerCase();
    return sortConfig.direction === SortDirection.asc
      ? nameA.localeCompare(nameB)
      : nameB.localeCompare(nameA);
  };

  const inProgress = inspections
    ?.filter((x) => !inspectionRecordService.isSubmitted(x.inspection))
    .filter(matchesSearch)
    .sort(bySortConfig);
  const submitted = inspections
    ?.filter((x) => inspectionRecordService.isSubmitted(x.inspection))
    .filter(matchesSearch)
    .sort(bySortConfig);

  const handleSortChange = (type: SortingType) => {
    setSortConfig((prev) => ({
      key: type,
      direction:
        prev.key === type
          ? prev.direction === SortDirection.asc
            ? SortDirection.desc
            : SortDirection.asc
          : SortDirection.asc,
    }));
  };

  const unsyncedCount = inspections?.filter((x) =>
    inspectionRecordService.isUndelivered(x.inspection),
  ).length;

  const handleSignOutClicked = () =>
    isPermissionError ? signOut() : requestSignOut();

  if (showLoadFailurePage) {
    return (
      <>
        <div className="flex min-h-screen flex-col items-center justify-center p-8 text-center">
          <div className="max-w-sm space-y-4">
            <div className="text-xl font-bold text-red-600">
              {isPermissionError ? 'Account not configured' : 'Failed to load'}
            </div>
            <p className="text-ink">
              {isPermissionError
                ? 'Your Insite Mobile account role has not been configured. Please contact digital support.'
                : 'Something went wrong loading your data. Try again when you have a connection.'}
            </p>
            {!isPermissionError && !!unsyncedCount && (
              <p className="font-bold text-ink">
                {`Signing out deletes ${formatCount(unsyncedCount, 'inspection')} not yet synced.`}
              </p>
            )}
            {/* A full sync keeps unsynced inspections through a failure; a retry keeps them, Sign Out does not. */}
            {!isPermissionError && (
              <Button
                variant="primary"
                className="w-full"
                type="button"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            )}
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => void handleSignOutClicked()}
            >
              Sign Out
            </Button>
          </div>
        </div>
        {signOutDialog}
      </>
    );
  }

  // A failed full-sync attempt clears the progress flag, but the query is still fetching while it waits to retry.
  const isFullSyncRunning =
    inspectionProgressMessage.isLoadingProgress ||
    (isFetching && hasNoCompletedSync);

  if (isFullSyncRunning) {
    return <LoadingPage loadingMessage={inspectionProgressMessage.msg ?? ''} />;
  }

  if (
    isLoading &&
    (initialDataLoadedState.isLoading ||
      !initialDataLoadedState.isInitialDataLoaded)
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  return (
    <div className="w-full">
      {inspectionProgressMessage.backgroundSyncError && (
        // pointer-events-none + auto on the dismiss: this full-width fixed banner overlaps
        // the New Inspection FAB (bottom-5 right-5, no z-index) - same click-swallowing
        // family as the toast strip. Only the ✕ needs to be clickable.
        <div className="pointer-events-none fixed right-4 bottom-16 left-4 z-50 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-normal text-red-900 shadow-lg">
          <span className="min-w-0 flex-1">
            {inspectionProgressMessage.backgroundSyncError}
          </span>
          <button
            onClick={() =>
              InspectionProgressMessage.set({ backgroundSyncError: null })
            }
            className="pointer-events-auto shrink-0 text-red-600 hover:text-red-800"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      {inspectionProgressMessage.msg && (
        // Purely informational and long-lived (background sync can run for minutes):
        // click-transparent so it never blocks the FAB underneath it.
        <div className="pointer-events-none fixed right-4 bottom-4 left-4 z-50 flex items-center gap-3 rounded-lg border border-brandLight bg-white px-4 py-3 text-sm font-normal text-primaryColor shadow-lg">
          <LoadingIndicator isLoading type="small" />
          <span className="min-w-0 truncate">
            {inspectionProgressMessage.msg}
          </span>
        </div>
      )}

      {/* Sticky: on a 1,100-card list the search bar must not scroll away
          exactly when it is needed. Same -top-4 full-bleed recipe as the
          sticky ScreenHeading (main has p-4). */}
      <div className="sticky -top-4 z-10 -mx-4 bg-white px-4 pt-4 pb-2">
        <SearchWithSorting
          searchValue={searchTerm}
          sortItems={[SortingType.date, SortingType.name]}
          handleSortChange={handleSortChange}
          onChangedSearch={(searchValue) => {
            setSearchTerm(searchValue);
            setInProgressVisible(PAGE_SIZE);
            setSubmittedVisible(PAGE_SIZE);
          }}
          sortConfig={sortConfig}
        />
      </div>

      {inspections === undefined ? (
        <LoadingIndicator isLoading center type="large" />
      ) : searchTerm && inProgress?.length === 0 && submitted?.length === 0 ? (
        <p className="py-12 text-center text-inkMuted">
          No inspections match your search.
        </p>
      ) : (
        <>
          {/* The working set first: these are the inspections the user came
              back to finish. While searching, a section with no matches is
              dropped entirely so the results read as one clean list. */}
          {!(searchTerm && inProgress?.length === 0) && (
            <div className="mb-8">
              <SectionHeading
                title={`In progress${inProgress ? ` (${inProgress.length})` : ''}`}
              />
              {inProgress?.length === 0 ? (
                <p className="py-6 text-inkMuted">
                  Nothing in progress - tap + to start an inspection.
                </p>
              ) : (
                <>
                  {/* Cards carry their own mb-4, so the grid only adds column gaps. */}
                  <div className="mt-2 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {inProgress?.slice(0, inProgressVisible).map((model) => (
                      <InspectionCard
                        key={model.inspection.externalId}
                        model={model}
                        showMenu={true}
                      />
                    ))}
                  </div>
                  {inProgress && inProgress.length > inProgressVisible && (
                    <Button
                      variant="outline"
                      fullWidth
                      className="my-2"
                      onClick={() => setInProgressVisible((n) => n + PAGE_SIZE)}
                    >
                      Show more ({inProgress.length - inProgressVisible} more)
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {!(searchTerm && submitted?.length === 0) && (
            <div className="mb-8">
              <SectionHeading
                title={`Submitted${submitted ? ` (${submitted.length})` : ''}`}
              />
              {submitted?.length === 0 ? (
                <p className="py-6 text-inkMuted">
                  No submitted inspections yet.
                </p>
              ) : (
                <>
                  <div className="mt-2 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {submitted?.slice(0, submittedVisible).map((model) => (
                      <InspectionCard
                        key={model.inspection.externalId}
                        model={model}
                        showMenu={true}
                      />
                    ))}
                  </div>
                  {submitted && submitted.length > submittedVisible && (
                    <Button
                      variant="outline"
                      fullWidth
                      className="my-2"
                      onClick={() => setSubmittedVisible((n) => n + PAGE_SIZE)}
                    >
                      Show more ({submitted.length - submittedVisible} more)
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={isOpenModal}
        useAnimation={true}
        onClose={() => setOpenModal(!isOpenModal)}
      >
        <div
          className={`fixed right-7 bottom-20 w-11/12 space-y-4 rounded-xl bg-white p-4 ${isOpenModal ? 'slide-in' : 'slide-out'}`}
        >
          <Button
            variant="secondary"
            className="w-full"
            type="button"
            onClick={(_) => navigate(routePaths.NewInspection)}
          >
            New inspection
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            type="button"
            onClick={(_) => navigate(routePaths.NewBucket)}
          >
            New bucket
          </Button>
          {/* "Changeout bucket" removed: it navigated to the SAME New-inspection
              screen (no changeout flow exists) - two labels, one destination. */}
          <div className={`tooltip-arrow ${isOpenModal ? 'active' : ''}`} />
        </div>
      </Modal>

      <FloatingButton onClick={() => setOpenModal(!isOpenModal)} />
    </div>
  );
}
